import { useState, useRef, useEffect } from 'react';
import { C, BUCKET_INFO, BUCKETS } from '../../theme';

/* ── Button ───────────────────────────────────────────────────────────────── */
export function Btn({ children, variant='blue', onClick, disabled, loading, className='', style }) {
  const base = 'elo-btn';
  return (
    <button
      className={`${base} btn-${variant} ${className}`}
      onClick={onClick}
      disabled={disabled || loading}
      style={style}
    >
      {loading ? <span className="spinner" /> : children}
    </button>
  );
}

/* ── Field ────────────────────────────────────────────────────────────────── */
export function Field({ label, filled, className='', ...props }) {
  return (
    <div className={`field-gap ${className}`}>
      {label && <div className="field-lbl">{label}</div>}
      <input className={`fi${filled ? ' filled' : ''}`} {...props} />
    </div>
  );
}

/* ── Textarea field ───────────────────────────────────────────────────────── */
export function TextareaField({ label, className='', ...props }) {
  return (
    <div className={`field-gap ${className}`}>
      {label && <div className="field-lbl">{label}</div>}
      <textarea className="fi" rows={3} {...props} />
    </div>
  );
}

/* ── Select field ─────────────────────────────────────────────────────────── */
export function SelectField({ label, options, className='', ...props }) {
  return (
    <div className={`field-gap ${className}`}>
      {label && <div className="field-lbl">{label}</div>}
      <select className="fi" {...props}>
        {options.map(o => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    </div>
  );
}

/* ── Hint box ─────────────────────────────────────────────────────────────── */
export function Hint({ children, amber }) {
  return (
    <div className={`hint${amber ? ' hint-amber' : ''}`}>
      <span style={{ flexShrink: 0, marginTop: 1 }}>
        {amber ? '⚠' : 'ℹ'}
      </span>
      <p>{children}</p>
    </div>
  );
}

/* ── Section label ────────────────────────────────────────────────────────── */
export function SecLabel({ children, style }) {
  return <div className="sec-lbl" style={style}>{children}</div>;
}

/* ── Section pill ─────────────────────────────────────────────────────────── */
export function SecPill({ children, bg, color }) {
  return (
    <span className="sec-pill" style={{ background: bg, color }}>{children}</span>
  );
}

/* ── Card ─────────────────────────────────────────────────────────────────── */
export function Card({ children, className='', style, onClick }) {
  return (
    <div className={`card ${className}`} style={style} onClick={onClick}>
      {children}
    </div>
  );
}

/* ── Journey dots ─────────────────────────────────────────────────────────── */
export function JourneyDots({ status }) {
  const cur =
    status === 'fully_complete'   ? 3 :
    status === 'debrief_complete' ? 2 :
    status === 'debrief_pending'  ? 1 : 0;
  const labels = ['Observed', 'Debriefed', 'Sent'];
  const colors = { done: C.blue, cur: C.amber };

  return (
    <div className="journey-steps">
      {[0,1,2].map(i => (
        <span key={i} style={{ display:'flex', alignItems:'center', gap:4 }}>
          {i > 0 && <span className={`js-line${i <= cur ? ' done':''}`} />}
          <span className={`js-dot ${i < cur ? 'done' : i === cur ? 'cur' : 'todo'}`}>{i+1}</span>
        </span>
      ))}
      <span className="js-label" style={{ color: cur === 3 ? C.blue : cur > 0 ? C.amber : C.text3 }}>
        {cur === 0 ? 'In progress' : labels[Math.min(cur-1,2)]}
      </span>
    </div>
  );
}

/* ── Bucket info modal (bottom sheet style) ───────────────────────────────── */
export function BucketModal({ bucketKey, onClose }) {
  const b    = BUCKETS[bucketKey];
  const info = BUCKET_INFO[bucketKey];
  if (!b || !info) return null;

  return (
    <div className="bucket-info-modal show" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bim-card animate-fade-up">
        <div className="bim-head">
          <div className="bim-dot" style={{ background: b.dot }} />
          <div className="bim-name">{b.name}</div>
          <button className="bim-close" onClick={onClose}>✕</button>
        </div>
        <div className="bim-q">{info.q}</div>
        <div className="bim-def">{info.def}</div>
        <div className="bim-sides">
          <div className="bim-side" style={{ background:'#E1F5EE18', border:'0.5px solid #9FE1CB' }}>
            <div className="bim-side-head" style={{ color: C.tealD }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:C.teal, display:'inline-block', marginRight:5 }} />
              Learning happening
            </div>
            {info.pos.map((e,i) => <div key={i} className="bim-ex" style={{ color: C.tealD }}>— {e}</div>)}
          </div>
          <div className="bim-side" style={{ background:'#FCEBEB18', border:'0.5px solid #F7C1C1' }}>
            <div className="bim-side-head" style={{ color: C.redD }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:C.red, display:'inline-block', marginRight:5 }} />
              Not happening
            </div>
            {info.neg.map((e,i) => <div key={i} className="bim-ex" style={{ color: C.redD }}>— {e}</div>)}
          </div>
        </div>
        <Btn variant="dark" onClick={onClose}>Got it — close</Btn>
      </div>
    </div>
  );
}

/* ── Student tag ──────────────────────────────────────────────────────────── */
export function StudentTag({ name }) {
  return (
    <span className="student-tag">
      <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
        <circle cx="5" cy="3.5" r="1.8" stroke={C.blueD} strokeWidth="1.2"/>
        <path d="M1.5 9c0-1.9 1.6-3 3.5-3s3.5 1.1 3.5 3" stroke={C.blueD} strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
      {name}
    </span>
  );
}

/* ── Progress bar (obs steps) ─────────────────────────────────────────────── */
export function ProgressBar({ steps, current }) {
  return (
    <div className="progress">
      <div className="prog-steps">
        {steps.map((step, i) => (
          <div key={step} className="prog-step">
            <div className={`prog-bar ${i < current ? 'done' : i === current ? 'cur' : ''}`} />
            <div className={`prog-name ${i === current ? 'cur' : ''}`}>{step}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Spinner ──────────────────────────────────────────────────────────────── */
export function Spinner({ size=24, color=C.blue }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `2px solid ${color}30`,
      borderTopColor: color,
      animation: 'spin 0.7s linear infinite',
      flexShrink: 0,
    }} />
  );
}

/* ── Avatar ───────────────────────────────────────────────────────────────── */
export function Avatar({ initials, bg=C.blueL, color=C.blueD, size=36 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color, display:'flex', alignItems:'center',
      justifyContent:'center', fontSize: size * 0.33, fontWeight: 600,
      flexShrink: 0, letterSpacing: '0.02em',
    }}>
      {initials}
    </div>
  );
}
